import styled from 'styled-components'
import { Avatar, CollapsibleItem } from '@lmc/ui-kit'
import DropdownIcon from '@lmc/ui-kit/src/dataDisplay/ListItem/DropdownIcon'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { ChangelogEntry } from './RevisionHistorySection'
import { formatDate, formatFieldChange, getFieldLabel } from './utils'
import { Text } from '@lmc/ui-kit/src/general'
import { Divider } from 'antd'

type RevisionHistoryItemProps = {
  entry: ChangelogEntry
  isDisposal?: boolean
  isRestock?: boolean
}

export const RevisionHistoryItem = ({
  entry,
  isDisposal,
  isRestock
}: RevisionHistoryItemProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)

  const changedFields = Object.keys(entry.changes)
  const changedFieldsLabels = changedFields.map((field) =>
    getFieldLabel(field, t, isDisposal, isRestock)
  )

  return (
    <CollapsibleItemStyled
      header={(toggleCollapse, collapsed) => (
        <Header onClick={toggleCollapse}>
          <HeaderContent>
            <LeftSection>
              <HeaderText>{formatDate(entry.changedAt)}</HeaderText>
              <UserSection>
                <AvatarWrapper>
                  <Avatar
                    url={entry.changedBy.picture as unknown as string}
                    size={20}
                  />
                </AvatarWrapper>
                <HeaderText>
                  {entry.changedBy.name || 'Unknown User'}
                </HeaderText>
              </UserSection>
            </LeftSection>
            <RightSection>
              <RightSectionText>
                {changedFieldsLabels.join(', ')} {t('CHANGED')}
              </RightSectionText>
              <ChevronIconWrapper>
                <RotatedDropdownIcon $isCollapsed={collapsed} />
              </ChevronIconWrapper>
            </RightSection>
          </HeaderContent>
        </Header>
      )}
      defaultCollapsed={true}
    >
      <Content>
        <StyledDivider />
        {changedFields.map((fieldName) => {
          const change = entry.changes[fieldName]
          const fieldLabel = getFieldLabel(fieldName, t, isDisposal, isRestock)
          const changeText = formatFieldChange(
            fieldName,
            change.old,
            change.new,
            t,
            entry.changedBy.name,
            isDisposal,
            isRestock
          )
          return (
            <ChangeRow key={fieldName}>
              <FieldNameColumn>
                <FieldNameText>{fieldLabel}:</FieldNameText>
              </FieldNameColumn>
              <ChangeDescriptionColumn>
                <ChangeText>{changeText}</ChangeText>
              </ChangeDescriptionColumn>
            </ChangeRow>
          )
        })}
      </Content>
    </CollapsibleItemStyled>
  )
}

const CollapsibleItemStyled = styled(CollapsibleItem)`
  background: ${({ theme }) => theme.color.neutral.color11};
  border: 1px solid ${({ theme }) => theme.color.neutral.color8};
  border-radius: 8px;
`

const Header = styled.div`
  cursor: pointer;
  width: 100%;
  padding: 16px;
`

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;
`

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 0 1 auto;
`

const HeaderText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  white-space: nowrap;
`

const RightSectionText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const AvatarWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

const ChevronIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 24px;
`

const RotatedDropdownIcon = styled(DropdownIcon)<{ $isCollapsed?: boolean }>`
  transform: ${(props) =>
    props.$isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)'};
  transition: transform 0.3s ease-in-out;
`

const StyledDivider = styled(Divider)`
  margin: 0;
`

const Content = styled.div`
  padding: 0 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ChangeRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  align-items: flex-start;
`

const FieldNameColumn = styled.div`
  width: 128px;
  flex-shrink: 0;
`

const FieldNameText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-weight: 700;
`

const ChangeDescriptionColumn = styled.div`
  flex: 1;
  min-width: 0;
`

const ChangeText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
  font-weight: 400;
`
